import { openDB, IDBPDatabase } from "idb";

const DB_NAME = "chatbot-storage";
const DB_VERSION = 1;
const STORE_NAME = "chatbot-data";

interface ChatbotDB {
  "chatbot-data": {
    key: string;
    value: string;
  };
}

let dbPromise: Promise<IDBPDatabase<ChatbotDB>> | null = null;

const getDB = (): Promise<IDBPDatabase<ChatbotDB>> | null => {
  if (typeof window === "undefined" || typeof indexedDB === "undefined") {
    return null;
  }

  if (!dbPromise) {
    dbPromise = openDB<ChatbotDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }

  return dbPromise;
};

/**
 * Get a value from IndexedDB storage
 * Falls back to localStorage if IndexedDB is not available
 */
export const getStorageItem = async (key: string): Promise<string | null> => {
  try {
    const db = getDB();
    if (db) {
      const database = await db;
      const value = await database.get(STORE_NAME, key);
      return value ?? null;
    }
  } catch (error) {
    console.warn(
      `[ChatbotStorage] IndexedDB read failed for key "${key}", falling back to localStorage:`,
      error,
    );
  }

  // Fallback to localStorage
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      console.warn(`[ChatbotStorage] localStorage read failed for key "${key}":`, e);
    }
  }

  return null;
};

/**
 * Set a value in IndexedDB storage
 * Also writes to localStorage for redundancy
 */
export const setStorageItem = async (key: string, value: string): Promise<void> => {
  // Always write to localStorage first for sync access reliability
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      console.warn(`[ChatbotStorage] localStorage write failed for key "${key}":`, e);
    }
  }

  // Then write to IndexedDB
  try {
    const db = getDB();
    if (db) {
      const database = await db;
      await database.put(STORE_NAME, value, key);
    }
  } catch (error) {
    console.warn(`[ChatbotStorage] IndexedDB write failed for key "${key}":`, error);
  }
};

/**
 * Remove a value from IndexedDB storage
 * Also removes from localStorage for consistency
 */
export const removeStorageItem = async (key: string): Promise<void> => {
  // Remove from localStorage first
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      console.warn(`[ChatbotStorage] localStorage remove failed for key "${key}":`, e);
    }
  }

  // Then remove from IndexedDB
  try {
    const db = getDB();
    if (db) {
      const database = await db;
      await database.delete(STORE_NAME, key);
    }
  } catch (error) {
    console.warn(`[ChatbotStorage] IndexedDB delete failed for key "${key}":`, error);
  }
};

// Storage keys constants
export const STORAGE_KEYS = {
  CHATBOT_USER_ID: "chatbot_user_id",
  CHATBOT_CONVERSATION_ID: "chatbot_conversation_id",
  CHATBOT_PREFERENCE_ID: "chatbot_perference_id",
  LEAD_FORM_DATA: "lead-form-one-saved-data",
  DISMISSED_PREFERENCES_FORMS: "chatbot_dismissed_preferences_forms",
  FOLLOW_UP_CHIPS: "chatbot_follow_up_chips",
  // Per-conversation CSAT nudge state (AI-1172): trigger, triggered/shown, rated
  CSAT_NUDGE_STATE: "chatbot_csat_nudge_state",
} as const;

// Synchronous getters for initial render (uses cached values or localStorage fallback)
const memoryCache: Map<string, string> = new Map();

// Track if storage has been initialized from IndexedDB
let storageInitialized = false;
let storageInitPromise: Promise<void> | null = null;

/**
 * Get a value synchronously from memory cache or localStorage
 * Use this only when async access is not possible (e.g., during initial render)
 */
export const getStorageItemSync = (key: string): string | null => {
  // Check memory cache first
  const cached = memoryCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  // Fallback to localStorage for sync access
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const value = window.localStorage.getItem(key);
      if (value !== null) {
        memoryCache.set(key, value);
      }
      return value;
    } catch (e) {
      console.warn(`[ChatbotStorage] localStorage sync read failed for key "${key}":`, e);
    }
  }

  return null;
};

/**
 * Check if storage has been initialized
 */
export const isStorageInitialized = (): boolean => storageInitialized;

/**
 * Wait for storage to be initialized
 * Returns immediately if already initialized
 * If not initialized, triggers initialization and waits for it
 */
export const waitForStorageInit = async (): Promise<void> => {
  if (storageInitialized) return;
  if (storageInitPromise) {
    await storageInitPromise;
    return;
  }
  // If not initialized and no promise exists, trigger initialization
  await initializeStorage();
};

/**
 * Initialize the storage by loading values from IndexedDB into memory cache
 * Call this early in the app lifecycle
 */
export const initializeStorage = async (): Promise<void> => {
  if (storageInitPromise) {
    return storageInitPromise;
  }

  if (storageInitialized) {
    return Promise.resolve();
  }

  storageInitPromise = (async () => {
    const keys = Object.values(STORAGE_KEYS);

    const results = await Promise.all(
      keys.map(async (key) => {
        try {
          const value = await getStorageItem(key);
          return { key, value };
        } catch (error) {
          return { key, value: null };
        }
      }),
    );

    results.forEach(({ key, value }) => {
      if (value !== null) {
        memoryCache.set(key, value);
        // Also ensure localStorage has the value for sync access reliability
        if (typeof window !== "undefined" && window.localStorage) {
          try {
            window.localStorage.setItem(key, value);
          } catch (e) {
            console.warn(
              `[ChatbotStorage] localStorage sync during init failed for key "${key}":`,
              e,
            );
          }
        }
      }
    });

    storageInitialized = true;
  })();

  return storageInitPromise;
};

/**
 * Update memory cache when setting a value
 */
export const setStorageItemWithCache = async (key: string, value: string): Promise<void> => {
  memoryCache.set(key, value);
  await setStorageItem(key, value);
};

/**
 * Clear memory cache when removing a value
 */
export const removeStorageItemWithCache = async (key: string): Promise<void> => {
  memoryCache.delete(key);
  await removeStorageItem(key);
};
